import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ResourceResponse } from "@shared/api/client";
import type {
  CommercialBenefits,
  CommercialCostForm,
  CommercialCostProfile,
  CommercialDashboard,
  CommercialFormOptions,
  CommercialLicense,
  CommercialSettingsPayload,
  CommercialSetting,
  CommercialValuation,
  CommercialValuationForm,
} from "../types";

const BASE = "/commercial";

export const commercialKeys = {
  all: ["commercial"] as const,
  dashboard: (emisorId: number) => [...commercialKeys.all, "dashboard", emisorId] as const,
  options: (emisorId: number) => [...commercialKeys.all, "options", emisorId] as const,
  valuations: (emisorId: number, filters: object) => [...commercialKeys.all, "valuations", emisorId, filters] as const,
  costs: (emisorId: number) => [...commercialKeys.all, "costs", emisorId] as const,
  benefits: (emisorId: number, filters: object) => [...commercialKeys.all, "benefits", emisorId, filters] as const,
  settings: (emisorId: number) => [...commercialKeys.all, "settings", emisorId] as const,
};

export function useCommercialDashboard(emisorId: number | null) {
  return useQuery({
    queryKey: commercialKeys.dashboard(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<CommercialDashboard>>(`${BASE}/dashboard`, { params: { emisor_id: emisorId } });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCommercialFormOptions(emisorId: number | null) {
  return useQuery({
    queryKey: commercialKeys.options(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<CommercialFormOptions>>(`${BASE}/form-options`, { params: { emisor_id: emisorId } });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCommercialValuations(
  emisorId: number | null,
  filters: { q?: string; estado?: string; desde?: string; hasta?: string },
) {
  return useQuery({
    queryKey: commercialKeys.valuations(emisorId ?? 0, filters),
    queryFn: async () => {
      const { data } = await api.get<{ data: CommercialValuation[] }>(`${BASE}/valuations`, {
        params: { emisor_id: emisorId, ...filters },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useSaveCommercialValuation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, id, payload }: { emisorId: number; id?: number; payload: CommercialValuationForm }) => {
      const request = id
        ? api.put<ResourceResponse<CommercialValuation>>(`${BASE}/valuations/${id}`, payload, { params: { emisor_id: emisorId } })
        : api.post<ResourceResponse<CommercialValuation>>(`${BASE}/valuations`, payload, { params: { emisor_id: emisorId } });
      return (await request).data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialKeys.all }),
  });
}

export function useApproveCommercialValuation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, id }: { emisorId: number; id: number }) =>
      (await api.post<ResourceResponse<CommercialValuation>>(`${BASE}/valuations/${id}/approve`, null, { params: { emisor_id: emisorId } })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialKeys.all }),
  });
}

export function useDeleteCommercialValuation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, id }: { emisorId: number; id: number }) =>
      api.delete(`${BASE}/valuations/${id}`, { params: { emisor_id: emisorId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialKeys.all }),
  });
}

export type CommercialValuationPdf = {
  blob: Blob;
  filename: string;
};

export async function fetchCommercialValuationPdf(
  emisorId: number,
  id: number,
): Promise<CommercialValuationPdf> {
  const response = await api.get<Blob>(`${BASE}/valuations/${id}/pdf`, {
    params: { emisor_id: emisorId },
    responseType: "blob",
  });

  const disposition = response.headers["content-disposition"] as string | undefined;
  const filenameMatch = disposition?.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] ?? `presupuesto-PRE-${new Date().getFullYear()}-${String(id).padStart(6, "0")}.pdf`;

  return { blob: response.data, filename };
}

export function saveCommercialValuationPdf(pdf: CommercialValuationPdf): void {
  const url = URL.createObjectURL(pdf.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = pdf.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function downloadCommercialValuationPdf(emisorId: number, id: number): Promise<void> {
  saveCommercialValuationPdf(await fetchCommercialValuationPdf(emisorId, id));
}

export function useCommercialCosts(emisorId: number | null) {
  return useQuery({
    queryKey: commercialKeys.costs(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<{ data: CommercialCostProfile[] }>(`${BASE}/costs`, { params: { emisor_id: emisorId } });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useUpdateCommercialCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, id, payload }: { emisorId: number; id: number; payload: CommercialCostForm }) =>
      (await api.put<ResourceResponse<CommercialCostProfile>>(`${BASE}/costs/${id}`, payload, { params: { emisor_id: emisorId } })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialKeys.all }),
  });
}

export function useCommercialBenefits(
  emisorId: number | null,
  filters: { q?: string; desde?: string; hasta?: string },
) {
  return useQuery({
    queryKey: commercialKeys.benefits(emisorId ?? 0, filters),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<CommercialBenefits>>(`${BASE}/benefits`, {
        params: { emisor_id: emisorId, ...filters },
      });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useCommercialSettings(emisorId: number | null) {
  return useQuery({
    queryKey: commercialKeys.settings(emisorId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<ResourceResponse<CommercialSettingsPayload>>(`${BASE}/settings`, { params: { emisor_id: emisorId } });
      return data.data;
    },
    enabled: !!emisorId,
  });
}

export function useUpdateCommercialSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, payload }: { emisorId: number; payload: Pick<CommercialSetting, "impuesto_pct" | "moneda"> }) =>
      (await api.put<ResourceResponse<CommercialSetting>>(`${BASE}/settings`, payload, { params: { emisor_id: emisorId } })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialKeys.all }),
  });
}

export function useSaveCommercialLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, id, payload }: { emisorId: number; id?: number; payload: Pick<CommercialLicense, "nombre" | "porcentaje" | "activo"> }) => {
      const request = id
        ? api.put<ResourceResponse<CommercialLicense>>(`${BASE}/licenses/${id}`, payload, { params: { emisor_id: emisorId } })
        : api.post<ResourceResponse<CommercialLicense>>(`${BASE}/licenses`, payload, { params: { emisor_id: emisorId } });
      return (await request).data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialKeys.all }),
  });
}

export function useDeleteCommercialLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ emisorId, id }: { emisorId: number; id: number }) =>
      api.delete(`${BASE}/licenses/${id}`, { params: { emisor_id: emisorId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialKeys.all }),
  });
}
